import PaginatedList from '@theme/paginated-list';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

/**
 * A custom element that renders a paginated blog posts list
 */
export default class BlogPostsList extends PaginatedList {
  /** @type {number | null} */
  #savedScrollTop = null;

  /** @type {boolean} */
  #pointerCausedFocus = false;

  connectedCallback() {
    super.connectedCallback();

    // Desktop squeeze uses `.page-wrapper` as the scroll container. Browser
    // default focus scrolling targets the wrong box and jumps the library
    // when a shopper clicks a card link (title / image / read more).
    this.addEventListener('pointerdown', this.#onPointerDown, true);
    this.addEventListener('focusin', this.#onFocusIn, true);
  }

  disconnectedCallback() {
    this.removeEventListener('pointerdown', this.#onPointerDown, true);
    this.removeEventListener('focusin', this.#onFocusIn, true);
    super.disconnectedCallback();
  }

  /**
   * @param {PointerEvent} event
   */
  #onPointerDown = (event) => {
    if (!(event.target instanceof Element)) return;
    if (!event.target.closest('a[href]')) return;

    this.#pointerCausedFocus = true;
    this.#savedScrollTop = getScrollTop();
  };

  #onFocusIn = () => {
    if (!this.#pointerCausedFocus) return;

    this.#pointerCausedFocus = false;
    const top = this.#savedScrollTop;
    this.#savedScrollTop = null;

    if (top == null) return;

    // Restore after the browser's focus scrollIntoView runs (may land
    // after the first paint frame on squeeze `.page-wrapper`).
    const restore = () => {
      if (getScrollTop() === top) return;
      scrollTo({ top, behavior: 'instant' });
    };
    requestAnimationFrame(() => {
      restore();
      requestAnimationFrame(restore);
    });
    setTimeout(restore, 0);
  };
}

if (!customElements.get('blog-posts-list')) {
  customElements.define('blog-posts-list', BlogPostsList);
}
